import React, { useState, useEffect } from 'react';
import { 
  Building2, Clock, Smartphone, ShieldCheck, ArrowLeft,
  Bell, Image as ImageIcon, MessageCircle, PhoneCall, Mail, LogOut, ChevronRight, X, AlertTriangle,
  Type, ZoomIn, ZoomOut, LayoutTemplate, Zap, Palette, Baseline, Volume2, Edit2
} from 'lucide-react';

const API_BASE = 'https://mukadampro.shirpur.store';

// --- INDIAN BANKS LIST ---
// --- LOCALIZED MALEGAON & INDIAN BANKS SMART LIST ---
const INDIAN_BANKS = [
    // 🌟 Malegaon Local & Co-operative Banks
    { name: "Malegaon Merchants Co-operative Bank", ifsc: "IBKL0533MMC" }, 
    { name: "The Malegaon Co-operative Bank Ltd", ifsc: "IBKL0533MMC" }, 
    { name: "Nashik District Central Co-op Bank (NDCC)", ifsc: "ICIC00NDCCB" }, 
    { name: "Janata Sahakari Bank ", ifsc: "HDFC0CJBMLG" }, 
    { name: "Shirpur Peoples Co-op Bank", ifsc: "KKBK0SPCB01" }, // Extra ifsc removed for correct 11-digit validation
    { name: "Nashik Rd Deolali Bank ", ifsc: "YESB0NRDB28" },
    { name: "Janalaxmi Coop Bank ", ifsc: "YESB0JLCB24" },
    { name: "Nashik Jilha Mahila Bank ", ifsc: "YESB0NMVB07" },
    
    // 🏦 Major Banks (Exact Malegaon Branches)
    { name: "State Bank of India ", ifsc: "SBIN0000418" },
    { name: "Bank of Maharashtra ", ifsc: "MAHB0000278" },
    { name: "HDFC Bank ", ifsc: "HDFC0002556" },
    { name: "ICICI Bank ", ifsc: "ICIC0006428" },
    { name: "Axis Bank ", ifsc: "UTIB0001240" },
    { name: "Bank of Baroda (Malegaon Camp)", ifsc: "BARB0DBMCAM" },
    { name: "Bank of Baroda (Malegaon City)", ifsc: "BARB0DBMLCT" },
    { name: "Union Bank of India ", ifsc: "UBIN0538787" },
    { name: "Punjab National Bank ", ifsc: "PUNB0785800" },
    { name: "Central Bank of India ", ifsc: "CBIN0280705" },
    { name: "Bank of India ", ifsc: "BKID0000813" },

    // 🇮🇳 Other Popular Indian Banks (Malegaon & Nashik Branches)
    { name: "Kotak Mahindra Bank (Soygaon)", ifsc: "KKBK0000699" },
    { name: "Kotak Mahindra Bank (Chandanpuri)", ifsc: "KKBK0001891" },
    { name: "IndusInd Bank ", ifsc: "INDB0001593" },
    { name: "Canara Bank ", ifsc: "CNRB0015281" },
    { name: "Yes Bank ", ifsc: "YESB0001091" }, 
    { name: "Indian Bank (Nasik City)", ifsc: "IDIB000N016" },  
    { name: "UCO Bank (Girnare)", ifsc: "UCBA0001521" }, 
    { name: "IDFC First Bank (Nashik - Mico Circle)", ifsc: "IDFB0042281" }, 
    { name: "IDFC First Bank (Nashik - Gangapur Road)", ifsc: "IDFB0042283" }, 
    { name: "IDFC First Bank (Pimpalgaon Baswant)", ifsc: "IDFB0042286" }, 
    { name: "IDFC First Bank (Nashik - Deolali Road)", ifsc: "IDFB0042289" }, 
    
    // 📱 Payments Banks (National Standard - Do not change)
    { name: "Airtel Payments Bank", ifsc: "AIRP0000001" },
    { name: "India Post Payments Bank", ifsc: "IPOS0000001" }
];

// --- PREMIUM SCROLLING TIME PICKER ---
const TimeScrollSelect = ({ value24h, onChange24h, label, onFocus }) => {
    let h24 = 8, m = '00', ampm = 'AM';
    if (value24h) {
        const [hh, mm] = value24h.split(':');
        h24 = parseInt(hh, 10);
        m = mm || '00';
        ampm = h24 >= 12 ? 'PM' : 'AM';
        if (h24 > 12) h24 -= 12;
        if (h24 === 0) h24 = 12;
    }
    const h12 = String(h24).padStart(2, '0');

    const handleUpdate = (newH, newM, newP) => {
        let hr = parseInt(newH, 10);
        if (newP === 'PM' && hr !== 12) hr += 12;
        if (newP === 'AM' && hr === 12) hr = 0;
        onChange24h(`${String(hr).padStart(2, '0')}:${newM}`);
    };

    const hoursArray = Array.from({length: 12}, (_, i) => String(i+1).padStart(2, '0'));
    const minsArray = Array.from({length: 60}, (_, i) => String(i).padStart(2, '0'));
    const ampmArray = ['AM', 'PM'];

    const scrollSelectStyle = { appearance: 'none', WebkitAppearance: 'none', background: 'transparent', border: 'none', padding: '6px 4px', fontSize: '14px', fontWeight: '700', color: 'inherit', outline: 'none', cursor: 'pointer', textAlign: 'center' };

    return (
        <div onClick={onFocus} style={{ display: 'flex', flexDirection: 'column', background: '#ffffff', borderRadius: '16px', padding: '12px', border: '1px solid #E2E8F0', flex: '1 1 130px', minWidth: '130px', boxShadow: '0 4px 10px rgba(0,0,0,0.02)', cursor: 'pointer', transition: 'all 0.2s' }}>
            <span style={{ fontSize: '9px', fontWeight: '600', color: '#64748B', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>{label}</span>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                <div style={{background: '#F8FAFC', borderRadius: '10px', padding: '2px', border: '1px solid #E2E8F0'}}>
                    <select value={h12} onChange={(e) => handleUpdate(e.target.value, m, ampm)} style={scrollSelectStyle}>
                        {hoursArray.map(hr => <option key={hr} value={hr}>{hr}</option>)}
                    </select>
                </div>
                <span style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A' }}>:</span>
                <div style={{background: '#F8FAFC', borderRadius: '10px', padding: '2px', border: '1px solid #E2E8F0'}}>
                    <select value={m} onChange={(e) => handleUpdate(h12, e.target.value, ampm)} style={scrollSelectStyle}>
                        {minsArray.map(mn => <option key={mn} value={mn}>{mn}</option>)}
                    </select>
                </div>
                <div style={{background: '#1A1A1A', borderRadius: '10px', padding: '2px', marginLeft: '4px', boxShadow: '0 4px 10px rgba(0,0,0,0.15)'}}>
                    <select value={ampm} onChange={(e) => handleUpdate(h12, m, e.target.value)} style={{...scrollSelectStyle, color: 'white', padding: '6px 6px', fontSize: '13px'}}>
                        {ampmArray.map(ap => <option key={ap} value={ap}>{ap}</option>)}
                    </select>
                </div>
            </div>
        </div>
    );
};

// --- HELPER CONVERTERS ---
const to24h = (time12h) => {
    const match = time12h.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return "08:00";
    let h = parseInt(match[1]); const m = match[2]; const p = match[3].toUpperCase();
    if (p === 'PM' && h !== 12) h += 12;
    if (p === 'AM' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${m}`;
};

const to12h = (time24h) => {
    const [hh, mm] = time24h.split(':');
    let h = parseInt(hh, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    if (h > 12) h -= 12;
    if (h === 0) h = 12;
    return `${String(h).padStart(2, '0')}:${mm} ${ampm}`;
};

const ProfileSettings = ({ onBack, onLogout, appBg, setAppBg, notifOn, setNotifOn, globalGuide }) => {
  const { handleVoiceClick, speakHinglish } = globalGuide;
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile'); 

  // --- SMART BANK SEARCH STATES ---
  const [bankSearch, setBankSearch] = useState('');
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [ifscStatus, setIfscStatus] = useState('idle'); // idle, loading, valid, invalid
  const [showIfscSearch, setShowIfscSearch] = useState(false);
  const [locState, setLocState] = useState('');
  const [locDistrict, setLocDistrict] = useState('');

  // Edit mode track karne ke liye state
  const [editMode, setEditMode] = useState({ business: false, owner: false, bank: false });
  const toggleEdit = (section) => setEditMode(prev => ({ ...prev, [section]: !prev[section] }));

const [formData, setFormData] = useState({
    textile_name: '', owner_name: '', mobile: '', email: '', 
    textile_address: '', gstin: '', total_looms: '', 
    pan_card: '', aadhar_card: '', owner_address: '', // ✅ owner_address add kiya
    bank_name: '', account_number: '', ifsc_code: '', upi_id: '', 
    account_holder_name: '', 
    shift_day_time: '08:00 AM - 08:00 PM', shift_night_time: '08:00 PM - 08:00 AM', 
    base_rate: '1.25', whatsapp_reports: 1
  });

const [loomList, setLoomList] = useState([]);
  const [assignedLooms, setAssignedLooms] = useState([]); // Visual Indicator ke liye
  const [newLoom, setNewLoom] = useState('');
  
  // Custom Premium Modal State
  const [deleteModal, setDeleteModal] = useState({ show: false, loomId: null, status: 'loading', workerName: '' });

  // 🚀 --- REAL-TIME APPEARANCE & UI ENGINE STATES ---
  const [appFont, setAppFont] = useState(localStorage.getItem('owner_appFont') || 'Plus Jakarta Sans');
  
  const [appBoxStyle, setAppBoxStyle] = useState(localStorage.getItem('owner_appBoxStyle') || 'glass');
  const [appBoxTexture, setAppBoxTexture] = useState(localStorage.getItem('owner_appBoxTexture') || 'none');
  const [appNeonGlow, setAppNeonGlow] = useState(parseInt(localStorage.getItem('owner_appNeonGlow')) || 0);

  const updateBoxTexture = (val) => { setAppBoxTexture(val); localStorage.setItem('owner_appBoxTexture', val); triggerAppUpdate(); triggerHaptic([20]); };
  const [appAuroraBg, setAppAuroraBg] = useState(localStorage.getItem('owner_appAuroraBg') !== 'false');
  const [appRippleEffect, setAppRippleEffect] = useState(localStorage.getItem('owner_appRippleEffect') !== 'false');
  const pageAnimStyle = 'fade';
  const appAnimSpeed = 'fast';
  localStorage.setItem('owner_pageAnimStyle', 'fade');
  localStorage.setItem('owner_appAnimSpeed', 'fast');
  
  const [appTextColor, setAppTextColor] = useState(localStorage.getItem('owner_appTextColor') || '#111111');
  const [appTextGlow, setAppTextGlow] = useState(parseInt(localStorage.getItem('owner_appTextGlow')) || 0);

  const updateTextGlow = (val) => { setAppTextGlow(val); localStorage.setItem('owner_appTextGlow', val); triggerAppUpdate(); triggerHaptic([20]); };


// --- VOICE AI STATES ---
  const [voiceSpeed, setVoiceSpeed] = useState(parseFloat(localStorage.getItem('owner_voiceSpeed')) || 1.0);
  const [selectedVoiceURI, setSelectedVoiceURI] = useState(localStorage.getItem('owner_selectedVoiceURI') || '');
  const [availableVoices, setAvailableVoices] = useState([]);

  // HAPTIC FEEDBACK ENGINE (Vibration)
  const triggerHaptic = (pattern = [50]) => {
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
          navigator.vibrate(pattern);
      }
  };

  useEffect(() => {
      const fetchVoices = () => {
          const voices = window.speechSynthesis.getVoices();
          const hindiVoices = voices.filter(v => v.lang.includes('hi') || v.lang.includes('IN')).slice(0, 5);
          setAvailableVoices(hindiVoices);
      };
      fetchVoices();
      window.speechSynthesis.onvoiceschanged = fetchVoices;
  }, []);

  const updateSettings = (key, val, setter) => {
      setter(val); localStorage.setItem('owner_' + key, val); triggerAppUpdate(); triggerHaptic([30]);
  };
  const updateVoiceSettings = (key, val, setter) => {
      setter(val); localStorage.setItem('owner_' + key, val); triggerHaptic([20]);
  };

  // App.js को रियल-टाइम अपडेट भेजने वाला सिग्नल (सिर्फ Owner के लिए)
  const triggerAppUpdate = () => { 
      window.dispatchEvent(new Event('ownerThemeChanged')); 
  };

  const updateBoxStyle = (style) => { 
      setAppBoxStyle(style); localStorage.setItem('owner_appBoxStyle', style); triggerAppUpdate(); 
  };

  const updateFont = (font) => { 
      setAppFont(font); localStorage.setItem('owner_appFont', font); triggerAppUpdate(); 
  };

  const updateTextColor = (color) => { setAppTextColor(color); localStorage.setItem('owner_appTextColor', color); triggerAppUpdate(); };

  // 5 New Premium Fonts Added
  const fonts = [
      'Plus Jakarta Sans', 'Poppins', 'Inter', 'Outfit', 'Quicksand', 
      'Montserrat', 'Raleway', 'Nunito', 'Playfair Display', 'Fira Sans'
  ];

  const textColors = [
      { id: 'black', hex: '#111111', name: 'Midnight Black' },
      { id: 'navy', hex: '#1E3A8A', name: 'Royal Navy' },
      { id: 'emerald', hex: '#064E3B', name: 'Deep Emerald' },
      { id: 'slate', hex: '#334155', name: 'Slate Grey' },
      { id: 'crimson', hex: '#881337', name: 'Crimson Red' },
      { id: 'white', hex: '#FFFFFF', name: 'Pure White' },
      // 👇 5 New Light & Glowing Neon Colors 👇
      { id: 'cyan', hex: '#00E5FF', name: 'Neon Cyan' },
      { id: 'hotpink', hex: '#FF1493', name: 'Hot Pink' },
      { id: 'gold', hex: '#FFB800', name: 'Glowing Gold' },
      { id: 'purple', hex: '#B026FF', name: 'Electric Purple' },
      { id: 'lime', hex: '#39FF14', name: 'Toxic Lime' }
  ];

  // Shift States
  const [dayStart24h, setDayStart24h] = useState('08:00');
  const [dayEnd24h, setDayEnd24h] = useState('20:00');
  const [nightStart24h, setNightStart24h] = useState('20:00');
  const [nightEnd24h, setNightEnd24h] = useState('08:00');

  const wallpapers = [
    { id: 1, color: "url('/1.jpg') center / cover no-repeat fixed", name: 'Wallpaper 1' },
    { id: 2, color: "url('/2.jpg') center / cover no-repeat fixed", name: 'Wallpaper 2' },
    { id: 3, color: "url('/3.jpg') center / cover no-repeat fixed", name: 'Wallpaper 3' },
    { id: 4, color: "url('/4.jpg') center / cover no-repeat fixed", name: 'Wallpaper 4' },
    { id: 5, color: "url('/5.jpg') center / cover no-repeat fixed", name: 'Wallpaper 5' },
    { id: 6, color: "url('/6.jpg') center / cover no-repeat fixed", name: 'Wallpaper 6' },
    { id: 7, color: "url('/7.jpg') center / cover no-repeat fixed", name: 'Wallpaper 7' },
    { id: 8, color: "url('/8.jpg') center / cover no-repeat fixed", name: 'Wallpaper 8' },
    { id: 9, color: "url('/9.jpg') center / cover no-repeat fixed", name: 'Wallpaper 9' },
    { id: 10, color: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)', name: 'Winter Sky' },
    { id: 11, color: 'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)', name: 'Lavender' },
  ];

  useEffect(() => {
    // 1. Business Profile Fetch
    fetch(`${API_BASE}/get_owner_profile.php`).then(res => res.json()).then(json => {
        if(json.status === 'success' && json.data) {
            const d = json.data;
            const flatData = { ...d.business_details, ...d.owner_details, ...d.bank_details };
            setFormData(prev => ({...prev, ...flatData}));
            if(flatData.shift_day_time) {
                const parts = flatData.shift_day_time.split(' - ');
                if(parts.length===2){ setDayStart24h(to24h(parts[0])); setDayEnd24h(to24h(parts[1])); }
            }
            if(flatData.shift_night_time) {
                const parts = flatData.shift_night_time.split(' - ');
                if(parts.length===2){ setNightStart24h(to24h(parts[0])); setNightEnd24h(to24h(parts[1])); }
            }
        }
    });

    // 2. Looms Fetch
    fetch(`${API_BASE}/manage_looms.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({action: 'get'})
    }).then(res => res.json()).then(data => {
        if(data.status === 'success' && data.active_looms) {
            setLoomList(data.active_looms.split(',').filter(Boolean));
            if(data.assigned_looms) setAssignedLooms(data.assigned_looms.split(',').filter(Boolean));
        }
    });

    // 3. ✨ NAYA CODE: Database se App Settings Lana ✨
    fetch(`${API_BASE}/manage_app_settings.php`)
      .then(res => res.json())
      .then(json => {
          if(json.status === 'success' && json.data) {
              const dbSettings = json.data;
              if(dbSettings.appFont) setAppFont(dbSettings.appFont);
              if(dbSettings.appBoxStyle) setAppBoxStyle(dbSettings.appBoxStyle);
              if(dbSettings.appBoxTexture) setAppBoxTexture(dbSettings.appBoxTexture);
              if(dbSettings.appTextColor) setAppTextColor(dbSettings.appTextColor);
              if(dbSettings.voiceSpeed) setVoiceSpeed(dbSettings.voiceSpeed);
              if(dbSettings.selectedVoiceURI) setSelectedVoiceURI(dbSettings.selectedVoiceURI);
              if(dbSettings.appNeonGlow !== undefined) setAppNeonGlow(dbSettings.appNeonGlow);
              if(dbSettings.appTextGlow !== undefined) setAppTextGlow(dbSettings.appTextGlow);
              if(dbSettings.appBg) setAppBg(dbSettings.appBg); // Wallpaper set
              if(dbSettings.notifOn !== undefined) setNotifOn(dbSettings.notifOn); // Notification set
          }
      }).catch(err => console.log("Settings Fetch Error", err));

  }, []);

  const handleAddLoom = async () => {
      if(!newLoom) return;
      try {
          const res = await fetch(`${API_BASE}/manage_looms.php`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({action: 'add', loom_id: newLoom})
          });
          const data = await res.json();
          if(data.status === 'success') {
              setLoomList(data.active_looms.split(',').filter(Boolean));
              setNewLoom(''); speakHinglish("नया लूम सफलतापूर्वक ऐड कर दिया गया है।");
          } else alert(data.message);
      } catch(e) { alert("Network Error"); }
  };

 // 1. Modal Open & Backend Status Check
  const triggerRemoveLoom = async (id) => {
      setDeleteModal({ show: true, loomId: id, status: 'loading', workerName: '' });
      try {
          const res = await fetch(`${API_BASE}/manage_looms.php`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({action: 'check_assignment', loom_id: id})
          });
          const data = await res.json();
          if(data.status === 'assigned') {
              setDeleteModal({ show: true, loomId: id, status: 'assigned', workerName: data.worker_name });
              speakHinglish(`यह लूम ${data.worker_name} को असाइन है।`);
          } else {
              setDeleteModal({ show: true, loomId: id, status: 'free', workerName: '' });
          }
      } catch(e) {
          setDeleteModal({ show: false, loomId: null, status: 'loading', workerName: '' });
          alert("Network Error! Checking failed.");
      }
  };

  // 2. Final Confirm Delete Action
  const confirmRemoveLoom = async () => {
      const id = deleteModal.loomId;
      setDeleteModal(prev => ({...prev, status: 'deleting'}));
      try {
          const res = await fetch(`${API_BASE}/manage_looms.php`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({action: 'force_remove', loom_id: id})
          });
          const data = await res.json();
          if(data.status === 'success') {
              setLoomList(data.active_looms.split(',').filter(Boolean));
              setAssignedLooms(prev => prev.filter(l => l !== id)); // Indicator update
              speakHinglish(`लूम नंबर ${id} हटा दिया गया है।`);
          } else { alert(data.message); }
      } catch(e) { alert("Network Error"); }
      setDeleteModal({ show: false, loomId: null, status: 'loading', workerName: '' });
  };

  const checkShiftClash = () => {
      const toMins = t => { const [h,m]=t.split(':').map(Number); return h*60+m; };
      const dS = toMins(dayStart24h), dE = toMins(dayEnd24h), nS = toMins(nightStart24h), nE = toMins(nightEnd24h);
      if (dS === dE) return "Day shift Start and End time cannot be the exact same.";
      if (nS === nE) return "Night shift Start and End time cannot be the exact same.";
      const getRange = (start, end) => {
          if (start < end) return [[start, end]];
          return [[start, 1440], [0, end]]; 
      };
      const dayRanges = getRange(dS, dE); const nightRanges = getRange(nS, nE);
      for (let [dStart, dEnd] of dayRanges) {
          for (let [nStart, nEnd] of nightRanges) {
              if (Math.max(dStart, nStart) < Math.min(dEnd, nEnd)) return "❌ Clash Detected! Day and Night shifts are overlapping. Please adjust the timings.";
          }
      }
      return null;
  };

  // --- SMART IFSC VERIFICATION ENGINE ---
  const handleIfscChange = async (val) => {
    const code = val.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setFormData(prev => ({ ...prev, ifsc_code: code }));

    if (code.length === 11) {
        setIfscStatus('loading');
        try {
            // Using standard public Razorpay IFSC API
            const res = await fetch(`https://ifsc.razorpay.com/${code}`);
            if (res.ok) {
                const data = await res.json();
                setFormData(prev => ({ 
                    ...prev, 
                    ifsc_code: code, 
                    // 👇 FIX: Agar user ne pehle se bank select kiya hai, toh Razorpay (IDBI) usko overwrite nahi karega
                    bank_name: prev.bank_name ? prev.bank_name : data.BANK 
                }));
                setIfscStatus('valid');
                triggerHaptic([20, 50, 20]);
                speakHinglish(`${data.BANK} वेरीफाई हो गया है।`);
            } else {
                setIfscStatus('invalid');
                speakHinglish("यह IFSC कोड गलत है, कृपया चेक करें।");
            }
        } catch (err) {
            setIfscStatus('invalid');
        }
    } else {
        setIfscStatus('idle');
    }
  };

  

  const handleSave = async (e) => {
    if(e) e.preventDefault();
    const clashError = checkShiftClash();
    if(clashError) {
        alert(clashError); speakHinglish("शिफ्ट टाइमिंग में क्लैश हो रहा है, कृपया सही टाइम सेट करें ताकि दोनों शिफ्ट एक-दूसरे से न टकराएँ।");
        return;
    }
    setLoading(true);
    
    const updatedFormData = {
        ...formData, shift_day_time: `${to12h(dayStart24h)} - ${to12h(dayEnd24h)}`, shift_night_time: `${to12h(nightStart24h)} - ${to12h(nightEnd24h)}`
    };

    // ✨ NAYA CODE: App Settings ka Data ikattha karna ✨
    const currentSettings = {
        appFont, appBoxStyle, appBoxTexture, appNeonGlow,
        appAuroraBg, appRippleEffect, appTextColor, appTextGlow,
        voiceSpeed, selectedVoiceURI, appBg, notifOn
    };

    try {
      // 1. Purana Owner Profile Update API
      const res = await fetch(`${API_BASE}/update_owner_profile.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updatedFormData)
      });

      // 2. ✨ NAYA CODE: App Settings ko Database me Save karna ✨
      await fetch(`${API_BASE}/manage_app_settings.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ settings: currentSettings })
      });

      const textResponse = await res.text(); 
      try {
        const data = JSON.parse(textResponse);
        if(data.status === 'success') { 
            speakHinglish(" सेटिंग्स सेव हो गई हैं। ये बदलाव वर्कर्स की अगली शिफ्ट से लागू होंगे।"); 
            alert(data.message); 
            // Data save hone ke baad fields wapas lock kar dein
            setEditMode({ business: false, owner: false, bank: false });
        } 
        else alert("Database Error: " + data.message);
      } catch (parseError) { alert("Server Error! Response: " + textResponse.substring(0, 100)); }
    } catch (err) { alert("Network Connection Error: " + err.message); } 
    finally { setLoading(false); }
  };

  const inputStyle = { width: '100%', padding: '14px', borderRadius: '14px', border: '1px solid #E2E8F0', fontSize: '13px', outline: 'none', background: '#F8FAFC', color:'#0F172A', fontWeight:'500', boxSizing: 'border-box', transition: 'all 0.2s ease' };
  const labelStyle = { fontSize: '9px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px', letterSpacing: '0.5px' };

  const SettingsItem = ({ icon, title, subtitle, onClick, voiceMsg, actionId }) => (
    <div onClick={(e) => handleVoiceClick(e, voiceMsg, onClick, actionId)} className="ui-setting-item" style={{display: 'flex', alignItems: 'center', padding: '14px', background: '#ffffff', borderRadius: '16px', marginBottom: '10px', border: '1px solid #E2E8F0', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 2px 8px rgba(0,0,0,0.02)'}}>
        <div style={{width:'40px', height:'40px', borderRadius:'12px', background:'#F8FAFC', display:'flex', alignItems:'center', justifyContent:'center', marginRight:'14px', flexShrink: 0, border: '1px solid #E2E8F0'}}>{icon}</div>
        <div style={{flex: 1, overflow: 'hidden'}}><div style={{fontSize: '13px', fontWeight: '700', color: '#0F172A', whiteSpace: 'nowrap', textOverflow: 'ellipsis'}}>{title}</div>{subtitle && <div style={{fontSize: '10px', color: '#64748B', marginTop: '2px', fontWeight: '600', whiteSpace: 'nowrap', textOverflow: 'ellipsis'}}>{subtitle}</div>}</div>
        <ChevronRight size={18} color="#94A3B8" style={{flexShrink: 0}} />
    </div>
  );

          // ===== 🌟 PREMIUM SCROLL LOCK (Hostinger / Mobile Fix) 🌟 =====
  // ProfileSettings khud ek full-screen overlay hai, isliye iske mount hote hi scroll lock kar denge
  useEffect(() => {
      document.body.style.overflow = 'hidden'; 
      return () => { document.body.style.overflow = 'auto'; };
  }, []);

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: appBg, zIndex: 3000, overflowY: 'auto', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      
      {/* 🚀 --- REAL-TIME GLOBAL STYLES INJECTOR --- */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;800;900&family=Outfit:wght@400;600;800;900&family=Poppins:wght@400;600;800;900&family=Quicksand:wght@400;600;700&family=Plus+Jakarta+Sans:wght@400;600;800;900&display=swap');
        
        /* 🌟 Yahan se hardcoded zoom aur font-size hata diya gaya hai taaki App.js ka global scale apply ho sake */
        body, html, * {
            font-family: '${appFont}', sans-serif !important;
            transition: background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1), 
                        transform 0.1s ease-out !important;
        }

        /* 👇 SARE PLACEHOLDERS KO LIGHT AUR PREMIUM BANANE KA CODE */
        input::placeholder, textarea::placeholder {
            color: #94A3B8 !important; 
            font-weight: 600 !important;
            opacity: 0.7 !important;
        } /* 🌟 Yahan bracket missing tha, jise theek kar diya gaya hai */ 

        ${appBoxStyle === 'solid' ? `
            .ui-card, .ui-setting-item {
                background: rgba(255, 255, 255, 0.95) !important;
                border: 1px solid rgba(0,0,0,0.08) !important;
                backdrop-filter: none !important;
                box-shadow: 0 10px 30px rgba(0,0,0,0.05) !important;
            }
        ` : appBoxStyle === 'neumorph' ? `
            .ui-card, .ui-setting-item {
                background: #f0f0f3 !important;
                border: none !important;
                backdrop-filter: none !important;
                box-shadow: 8px 8px 16px #d1d1d4, -8px -8px 16px #ffffff !important;
            }
        ` : '' }
      `}</style>

      <div style={{ background: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', padding: '16px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: '12px', position:'sticky', top:0, zIndex:10 }}>
          <button onClick={(e) => handleVoiceClick(e, "बैक जा रहे हैं।", onBack, 'btn-back')} style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #E2E8F0', cursor: 'pointer', padding: '8px', display:'flex', alignItems:'center', boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}>
              <ArrowLeft size={18} color="#0F172A" />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ background: 'linear-gradient(135deg, #1E293B, #0F172A)', padding: '6px', borderRadius: '8px', boxShadow: '0 4px 10px rgba(0,0,0,0.15)', display: 'flex' }}><ShieldCheck color="white" size={16} /></div>
            <h1 style={{ fontSize: '18px', fontWeight: '700', color: '#0F172A', margin: 0, letterSpacing:'-0.5px' }}>Control Center</h1>
          </div>
      </div>

      <div style={{ padding: '16px', paddingBottom: '120px', maxWidth: '600px', margin: '0 auto' }}>
        
        {/* PREMIUM TAB SWITCHER */}
        <div style={{ display: 'flex', background: '#F1F5F9', borderRadius: '16px', padding: '4px', marginBottom: '24px', border: '1px solid #E2E8F0' }}>
          <button onClick={() => setActiveTab('profile')} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: 'none', background: activeTab === 'profile' ? '#ffffff' : 'transparent', color: activeTab === 'profile' ? '#0F172A' : '#64748B', fontWeight: '700', fontSize: '12px', cursor: 'pointer', boxShadow: activeTab === 'profile' ? '0 4px 12px rgba(0,0,0,0.05)' : 'none', transition: 'all 0.2s' }}>
            Business Profile
          </button>
          <button onClick={() => setActiveTab('settings')} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: 'none', background: activeTab === 'settings' ? '#ffffff' : 'transparent', color: activeTab === 'settings' ? '#0F172A' : '#64748B', fontWeight: '700', fontSize: '12px', cursor: 'pointer', boxShadow: activeTab === 'settings' ? '0 4px 12px rgba(0,0,0,0.05)' : 'none', transition: 'all 0.2s' }}>
            App Settings
          </button>
        </div>

        {/* ---------------- PROFILE SECTION ---------------- */}
        {activeTab === 'profile' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div className="ui-card" style={{ background: '#ffffff', borderRadius: '24px', padding: '20px', border: '1px solid #E2E8F0', boxShadow: '0 4px 15px rgba(0,0,0,0.03)' }}>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>                
                
                {/* --- BUSINESS DETAILS --- */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px', marginBottom: '-8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '700', color: '#2563EB' }}>
                        <div style={{ background: 'linear-gradient(135deg, #E0F2FE, #7DD3FC)', padding: '4px', borderRadius: '6px', display: 'flex', boxShadow: '0 2px 4px rgba(2, 132, 199, 0.15)' }}>
                            <Building2 size={14} color="#0284C7" />
                        </div>
                        BUSINESS DETAILS
                    </div>
                    <div onClick={() => toggleEdit('business')} style={{ cursor: 'pointer', background: editMode.business ? '#10B981' : '#F1F5F9', padding: '4px 10px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px', transition: 'all 0.2s', boxShadow: editMode.business ? '0 2px 6px rgba(16, 185, 129, 0.2)' : 'none' }}>
                        {editMode.business ? <ShieldCheck size={12} color="#ffffff" /> : <Edit2 size={12} color="#64748B" />}
                        <span style={{ fontSize: '10px', fontWeight: '600', color: editMode.business ? '#ffffff' : '#64748B', letterSpacing: '0.5px' }}>{editMode.business ? 'SAVE' : 'EDIT'}</span>
                    </div>
                </div>
                
                <div><label style={labelStyle}>Textile Name</label><input readOnly={!editMode.business} style={{...inputStyle, background: editMode.business ? '#ffffff' : '#F8FAFC', border: editMode.business ? '1px solid #3B82F6' : '1px solid transparent', color: editMode.business ? '#0F172A' : '#64748B'}} value={formData.textile_name} onChange={e => setFormData({...formData, textile_name: e.target.value})} /></div>
                <div><label style={labelStyle}>Textile Address</label><textarea readOnly={!editMode.business} style={{...inputStyle, height: '60px', resize: 'none', background: editMode.business ? '#ffffff' : '#F8FAFC', border: editMode.business ? '1px solid #3B82F6' : '1px solid transparent', color: editMode.business ? '#0F172A' : '#64748B'}} value={formData.textile_address} onChange={e => setFormData({...formData, textile_address: e.target.value})} /></div>
                <div><label style={labelStyle}>GSTIN Number</label><input readOnly={!editMode.business} style={{...inputStyle, textTransform: 'uppercase', background: editMode.business ? '#ffffff' : '#F8FAFC', border: editMode.business ? '1px solid #3B82F6' : '1px solid transparent', color: editMode.business ? '#0F172A' : '#64748B'}} value={formData.gstin} onChange={e => setFormData({...formData, gstin: e.target.value})} /></div>

               {/* --- OWNER DETAILS --- */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px', marginTop: '8px', marginBottom: '-8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '700', color: '#2563EB' }}>
                        <div style={{ background: 'linear-gradient(135deg, #DBEAFE, #93C5FD)', padding: '4px', borderRadius: '6px', display: 'flex', boxShadow: '0 2px 4px rgba(37, 99, 235, 0.15)' }}>
                            <ShieldCheck size={14} color="#2563EB" />
                        </div>
                        OWNER DETAILS
                    </div>
                    <div onClick={() => toggleEdit('owner')} style={{ cursor: 'pointer', background: editMode.owner ? '#10B981' : '#F1F5F9', padding: '4px 10px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px', transition: 'all 0.2s', boxShadow: editMode.owner ? '0 2px 6px rgba(16, 185, 129, 0.2)' : 'none' }}>
                        {editMode.owner ? <ShieldCheck size={12} color="#ffffff" /> : <Edit2 size={12} color="#64748B" />}
                        <span style={{ fontSize: '10px', fontWeight: '600', color: editMode.owner ? '#ffffff' : '#64748B', letterSpacing: '0.5px' }}>{editMode.owner ? 'SAVE' : 'EDIT'}</span>
                    </div>
                </div>
                
                <div><label style={labelStyle}>Owner Name</label><input readOnly={!editMode.owner} style={{...inputStyle, background: editMode.owner ? '#ffffff' : '#F8FAFC', border: editMode.owner ? '1px solid #3B82F6' : '1px solid transparent', color: editMode.owner ? '#0F172A' : '#64748B'}} value={formData.owner_name} onChange={e => setFormData({...formData, owner_name: e.target.value})} /></div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '16px' }}>
                  <div><label style={labelStyle}>Mobile</label><input type="tel" readOnly={!editMode.owner} style={{...inputStyle, background: editMode.owner ? '#ffffff' : '#F8FAFC', border: editMode.owner ? '1px solid #3B82F6' : '1px solid transparent', color: editMode.owner ? '#0F172A' : '#64748B'}} value={formData.mobile} onChange={e => setFormData({...formData, mobile: e.target.value})} /></div>
                  <div><label style={labelStyle}>Email ID</label><input type="email" readOnly={!editMode.owner} style={{...inputStyle, background: editMode.owner ? '#ffffff' : '#F8FAFC', border: editMode.owner ? '1px solid #3B82F6' : '1px solid transparent', color: editMode.owner ? '#0F172A' : '#64748B'}} value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} /></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '16px' }}>
                  <div><label style={labelStyle}>PAN Card</label><input readOnly={!editMode.owner} style={{...inputStyle, textTransform: 'uppercase', background: editMode.owner ? '#ffffff' : '#F8FAFC', border: editMode.owner ? '1px solid #3B82F6' : '1px solid transparent', color: editMode.owner ? '#0F172A' : '#64748B'}} value={formData.pan_card} onChange={e => setFormData({...formData, pan_card: e.target.value})} /></div>
                  <div><label style={labelStyle}>Aadhar Card</label><input type="number" readOnly={!editMode.owner} style={{...inputStyle, background: editMode.owner ? '#ffffff' : '#F8FAFC', border: editMode.owner ? '1px solid #3B82F6' : '1px solid transparent', color: editMode.owner ? '#0F172A' : '#64748B'}} value={formData.aadhar_card} onChange={e => setFormData({...formData, aadhar_card: e.target.value})} /></div>
                </div>
                {/* ✅ Owner Address Field */}
                <div><label style={labelStyle}>Owner Address</label><textarea readOnly={!editMode.owner} style={{...inputStyle, height: '60px', resize: 'none', background: editMode.owner ? '#ffffff' : '#F8FAFC', border: editMode.owner ? '1px solid #3B82F6' : '1px solid transparent', color: editMode.owner ? '#0F172A' : '#64748B'}} value={formData.owner_address} onChange={e => setFormData({...formData, owner_address: e.target.value})} placeholder="Enter Owner Address" /></div>
                
              {/* --- BANK & SETTLEMENT DETAILS (FOR INVOICING) --- */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px', marginBottom: '12px', marginTop: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '700', color: '#4338CA' }}>
                      <div style={{ background: 'linear-gradient(135deg, #E0E7FF, #A5B4FC)', padding: '4px', borderRadius: '6px', display: 'flex', boxShadow: '0 2px 4px rgba(67, 56, 202, 0.15)' }}>
                          <Building2 size={14} color="#4338CA" />
                      </div>
                      BANK DETAILS (FOR BILLING)
                  </div>
                  <div onClick={() => toggleEdit('bank')} style={{ cursor: 'pointer', background: editMode.bank ? '#10B981' : '#F1F5F9', padding: '4px 10px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px', transition: 'all 0.2s', boxShadow: editMode.bank ? '0 2px 6px rgba(16, 185, 129, 0.2)' : 'none' }}>
                      {editMode.bank ? <ShieldCheck size={12} color="#ffffff" /> : <Edit2 size={12} color="#64748B" />}
                      <span style={{ fontSize: '10px', fontWeight: '600', color: editMode.bank ? '#ffffff' : '#64748B', letterSpacing: '0.5px' }}>{editMode.bank ? 'SAVE' : 'EDIT'}</span>
                  </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Account Number</label>
                  <input 
                      type="number" 
                      readOnly={!editMode.bank} 
                      style={{...inputStyle, background: editMode.bank ? '#ffffff' : '#F8FAFC', border: editMode.bank ? '1px solid #4338CA' : '1px solid transparent', color: editMode.bank ? '#0F172A' : '#64748B'}} 
                      value={formData.account_number} 
                      onChange={e => setFormData({...formData, account_number: e.target.value})} 
                      placeholder="Enter Account No."
                  />
              </div>
              
              {/* 🚀 STEP 1: SELECT BANK ONLY */}
              <div style={{ background: '#F8FAFC', padding: '16px', borderRadius: '16px', border: '1px solid #E2E8F0', marginBottom: '16px' }}>
                  <label style={{...labelStyle, color: '#0F172A'}}>🏦 1. Select Bank</label>
                  <div style={{ marginTop: '12px' }}>
                      <select 
                          disabled={!editMode.bank}
                          style={{
                              ...inputStyle, 
                              background: editMode.bank ? '#ffffff' : '#F1F5F9', 
                              color: formData.bank_name ? '#0F172A' : '#94A3B8',
                              appearance: editMode.bank ? 'auto' : 'none' 
                          }}
                          value={formData.bank_name || ''}
                          onChange={e => {
                              // 👇 FIX: Sirf bank name set hoga, IFSC direct auto-fill nahi hoga
                              setFormData({...formData, bank_name: e.target.value, ifsc_code: ''});
                              setIfscStatus('idle');
                              setShowIfscSearch(false); // Naya bank chuna toh search box band kardo
                          }}
                      >
                          {formData.bank_name && !INDIAN_BANKS.some(b => b.name === formData.bank_name) && (
                              <option value={formData.bank_name}>{formData.bank_name}</option>
                          )}
                          <option value="">Select Bank...</option>
                          {INDIAN_BANKS.map((b, i) => <option key={i} value={b.name}>{b.name}</option>)}
                      </select>
                  </div>
              </div>

              {/* 🚀 STEP 2: CONNECTED SEARCH UI & FINAL IFSC DISPLAY */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                 <div style={{ position: 'relative' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <label style={{...labelStyle, margin: 0}}>
                              Exact IFSC Code
                              {ifscStatus === 'loading' && <span style={{color: '#F59E0B', marginLeft: '6px', fontSize: '9px'}}>Verifying...</span>}
                          </label>
                          
                          {/* 👇 CHHOTA SA SEARCH TEXT BUTTON */}
                          <span 
                              onClick={() => {
                                  if(!editMode.bank) return;
                                  if(!formData.bank_name) { speakHinglish("पहले बैंक सेलेक्ट करें"); return; }
                                  setShowIfscSearch(!showIfscSearch);
                              }} 
                              style={{ fontSize: '9px', fontWeight: '600', color: editMode.bank ? '#2563EB' : '#94A3B8', cursor: editMode.bank ? 'pointer' : 'not-allowed', background: '#DBEAFE', padding: '4px 8px', borderRadius: '6px', transition: 'all 0.2s' }}
                          >
                              {showIfscSearch ? '✖ CLOSE' : '🔍 SEARCH IFSC'}
                          </span>
                      </div>

                      {/* 👇 CONNECTED COMPACT SEARCH BOX */}
                      {showIfscSearch && editMode.bank && (
                          <div style={{ background: '#F8FAFC', padding: '10px', borderRadius: '10px', border: '1px solid #93C5FD', marginBottom: '4px', display: 'flex', gap: '6px', flexDirection: 'column', animation: 'fadeIn 0.2s ease' }}>
                              <select style={{...inputStyle, padding: '8px', fontSize: '11px'}} value={locState} onChange={e => setLocState(e.target.value)}>
                                  <option value="">Select State...</option>
                                  <option value="Maharashtra">Maharashtra</option>
                              </select>
                              
                              <select disabled={!locState} style={{...inputStyle, padding: '8px', fontSize: '11px'}} value={locDistrict} onChange={e => setLocDistrict(e.target.value)}>
                                  <option value="">Select District...</option>
                                  <option value="Nashik">Nashik</option>
                                  <option value="Dhule">Dhule</option>
                              </select>

                              <select 
                                  disabled={!locDistrict} 
                                  style={{...inputStyle, padding: '8px', fontSize: '11px', border: '1px solid #3B82F6', background: '#EFF6FF', color: '#1D4ED8', fontWeight: '700'}} 
                                  onChange={e => {
                                      const village = e.target.value;
                                      if(village === 'Malegaon') {
                                          const foundBank = INDIAN_BANKS.find(b => b.name === formData.bank_name);
                                          if (foundBank && foundBank.ifsc) {
                                              handleIfscChange(foundBank.ifsc); // Turant API Verification and Audio Trigger hoga
                                              setShowIfscSearch(false); // Box automatic close ho jayega
                                          } else {
                                              speakHinglish("इस बैंक का मालेगांव ब्रांच उपलब्ध नहीं है, कृपया मैन्युअल टाइप करें।");
                                          }
                                      }
                                  }}
                              >
                                  <option value="">Select City/Village...</option>
                                  {locDistrict === 'Nashik' && <option value="Malegaon">Malegaon (Auto-fill)</option>}
                                  {locDistrict === 'Nashik' && <option value="Nashik City">Nashik City</option>}
                                  {locDistrict === 'Dhule' && <option value="Shirpur">Shirpur</option>}
                              </select>
                          </div>
                      )}

                     {/* Premium Clean Input with Inner Green Tick */}
                      <div style={{ position: 'relative' }}>
                          <input 
                              readOnly={!editMode.bank} 
                              style={{
                                  ...inputStyle, 
                                  background: editMode.bank ? '#ffffff' : '#F8FAFC', 
                                  border: editMode.bank ? (showIfscSearch ? '1px solid #93C5FD' : '1px solid #4338CA') : '1px solid transparent', 
                                  color: '#0F172A', // Hamesha dark professional color rahega
                                  textTransform: 'uppercase',
                                  paddingRight: (formData.ifsc_code && formData.ifsc_code.length === 11) ? '36px' : '14px' // Tick ke liye space
                              }} 
                              value={formData.ifsc_code} 
                              onChange={e => handleIfscChange(e.target.value)} 
                              placeholder="Enter IFSC Code"
                              maxLength={11}
                          />
                          
                          {/* Verified Green Tick Logo */}
                          {formData.ifsc_code && formData.ifsc_code.length === 11 && ifscStatus !== 'invalid' && (
                              <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>
                                  <ShieldCheck size={18} color="#10B981" />
                              </div>
                          )}
                      </div>
                  </div>
                  <div>
                      <label style={labelStyle}>UPI ID / QR Link</label>
                      <input 
                          readOnly={!editMode.bank} 
                          style={{...inputStyle, background: editMode.bank ? '#ffffff' : '#F8FAFC', border: editMode.bank ? '1px solid #4338CA' : '1px solid transparent'}} 
                          value={formData.upi_id} 
                          onChange={e => setFormData({...formData, upi_id: e.target.value})} 
                          placeholder="e.g. xxxxxxxxxx@ybl" 
                      />
                  </div>
              </div>


                {/* ✅ OPERATION SETTINGS (SHIFTS & RATES) WITH ICON */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px', marginBottom: '12px', marginTop: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '700', color: '#059669' }}>
                        <div style={{ background: 'linear-gradient(135deg, #D1FAE5, #6EE7B7)', padding: '4px', borderRadius: '6px', display: 'flex', boxShadow: '0 2px 4px rgba(5, 150, 105, 0.15)' }}>
                            <Zap size={14} color="#059669" />
                        </div>
                        OPERATION SETTINGS
                    </div>
                </div>

                {/* SMART LOOM MANAGER (Moved inside Operation Settings) */}
                <div style={{ gridColumn: '1 / -1', background: '#F8FAFC', padding: '16px', borderRadius: '16px', border: '1px solid #E2E8F0', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <label style={{...labelStyle, margin: 0}}>Smart Loom Manager</label>
                        <span style={{ fontSize: '10px', fontWeight: '600', color: '#2563EB', background: 'rgba(37,99,235,0.1)', padding: '4px 8px', borderRadius: '8px' }}>Total Active: {loomList.length}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                        <input type="number" placeholder="Enter Loom No." value={newLoom} onChange={e => setNewLoom(e.target.value)} style={{...inputStyle, flex: '1 1 100px', padding: '12px', background: '#ffffff'}} />
                        <button onClick={(e) => { e.preventDefault(); handleAddLoom(); }} type="button" style={{ background: '#1A1A1A', color: 'white', border: 'none', borderRadius: '14px', padding: '12px 20px', fontWeight: '700', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 4px 10px rgba(0,0,0,0.15)' }}>+ ADD</button>
                    </div>
                    {/* Grid size adjusted slightly for "Loom 13" format */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(85px, 1fr))', gap: '14px', maxHeight: '200px', overflowY: 'auto', padding: '8px 4px 8px 0' }}>
                        {loomList.map(l => {
                            const isAssigned = assignedLooms.includes(String(l));
                            return (
                                <div key={l} style={{ position: 'relative', background: '#ffffff', padding: '12px 0', borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: isAssigned ? '1.5px solid #2563EB' : '1px solid #E2E8F0', boxShadow: '0 2px 6px rgba(0,0,0,0.03)', transition: 'all 0.2s' }}>
                                    
                                    {/* VISUAL INDICATOR: Green/Grey Dot (Top Left) */}
                                    <div style={{ position: 'absolute', top: '6px', left: '6px', width: '6px', height: '6px', borderRadius: '50%', background: isAssigned ? '#10B981' : '#CBD5E1', boxShadow: isAssigned ? '0 0 6px rgba(16,185,129,0.4)' : 'none' }} title={isAssigned ? 'Assigned' : 'Free'}></div>
                                    
                                    {/* TEXT FORMAT: Loom 13 AND ALWAYS BLACK TEXT */}
                                    <span style={{ fontSize: '13px', fontWeight: '700', color: '#0F172A' }}>Loom {l}</span>
                                    
                                    {/* CROSS ICON ONLY SHOWS IF LOOM IS INACTIVE (NOT ASSIGNED) */}
                                    {!isAssigned && (
                                        <div onClick={() => triggerRemoveLoom(l)} style={{ position: 'absolute', top: '-6px', right: '-6px', width: '18px', height: '18px', background: '#FEF2F2', borderRadius: '50%', border: '1px solid #FCA5A5', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 2, boxShadow: '0 2px 4px rgba(239, 68, 68, 0.15)', transition: 'transform 0.2s' }} onMouseDown={(e)=>e.currentTarget.style.transform='scale(0.8)'} onMouseUp={(e)=>e.currentTarget.style.transform='scale(1)'}>
                                            <X size={10} color="#EF4444" strokeWidth={3.5} />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    
                    {/* --- CUSTOM PREMIUM POPUP MODAL --- */}
                    {deleteModal.show && (
                        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(5px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', boxSizing: 'border-box' }}>
                            <div style={{ background: '#ffffff', width: '100%', maxWidth: '320px', borderRadius: '24px', padding: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', textAlign: 'center', animation: 'fadeIn 0.2s ease-out' }}>
                                
                                {deleteModal.status === 'loading' ? (
                                    <div style={{ padding: '20px' }}>
                                        <div style={{ width: '40px', height: '40px', border: '3px solid #E2E8F0', borderTop: '3px solid #3B82F6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
                                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#64748B' }}>Checking Loom Status...</div>
                                    </div>
                                ) : (
                                    <>
                                        <div style={{ width: '52px', height: '52px', borderRadius: '16px', background: deleteModal.status === 'assigned' ? '#FEF2F2' : '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', border: deleteModal.status === 'assigned' ? '1px solid #FECACA' : '1px solid #E2E8F0' }}>
                                            {deleteModal.status === 'assigned' ? <AlertTriangle size={24} color="#EF4444" /> : <X size={24} color="#64748B" />}
                                        </div>
                                        
                                        <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0F172A', margin: '0 0 8px 0' }}>
                                            {deleteModal.status === 'assigned' ? `Loom ${deleteModal.loomId} Is Active!` : `Delete Loom ${deleteModal.loomId}?`}
                                        </h3>
                                        
                                        {deleteModal.status === 'assigned' ? (
                                            <p style={{ fontSize: '12px', color: '#475569', margin: '0 0 24px 0', lineHeight: '1.6', fontWeight: '500' }}>
                                                यह लूम अभी <strong style={{ color: '#EF4444', background: '#FEF2F2', padding: '2px 6px', borderRadius: '4px' }}>{deleteModal.workerName}</strong> को असाइन है। चालू लूम को डिलीट नहीं किया जा सकता।
                                            </p>
                                        ) : (
                                            <p style={{ fontSize: '12px', color: '#64748B', margin: '0 0 24px 0', fontWeight: '500' }}>
                                                यह लूम अभी खाली है। क्या आप इसे परमानेंट हटाना चाहते हैं?
                                            </p>
                                        )}
                                        
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            {deleteModal.status === 'assigned' ? (
                                                <button onClick={() => setDeleteModal({ show: false, loomId: null, status: 'loading', workerName: '' })} style={{ flex: 1, padding: '12px', borderRadius: '14px', background: '#3B82F6', color: '#ffffff', border: 'none', fontWeight: '700', fontSize: '12px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(59,130,246,0.2)' }}>
                                                    OKAY, UNDERSTOOD
                                                </button>
                                            ) : (
                                                <>
                                                    <button onClick={() => setDeleteModal({ show: false, loomId: null, status: 'loading', workerName: '' })} style={{ flex: 1, padding: '12px', borderRadius: '14px', background: '#F8FAFC', color: '#64748B', border: '1px solid #E2E8F0', fontWeight: '600', fontSize: '12px', cursor: 'pointer' }}>
                                                        CANCEL
                                                    </button>
                                                    <button onClick={confirmRemoveLoom} disabled={deleteModal.status === 'deleting'} style={{ flex: 1, padding: '12px', borderRadius: '14px', background: '#EF4444', color: '#ffffff', border: 'none', fontWeight: '700', fontSize: '12px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(239,68,68,0.2)' }}>
                                                        {deleteModal.status === 'deleting' ? 'WAIT...' : 'YES, DELETE'}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}
                                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                            </div>
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ padding: '16px', background: '#F8FAFC', borderRadius: '16px', border: '1px solid #E2E8F0' }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#0F172A', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{width:'10px', height:'10px', background:'#F59E0B', borderRadius:'50%'}}></span> DAY SHIFT</div>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            <TimeScrollSelect label="Start Time" value24h={dayStart24h} onChange24h={setDayStart24h} />
                            <TimeScrollSelect label="End Time" value24h={dayEnd24h} onChange24h={setDayEnd24h} />
                        </div>
                    </div>
                    <div style={{ padding: '16px', background: '#F8FAFC', borderRadius: '16px', border: '1px solid #E2E8F0' }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#0F172A', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{width:'10px', height:'10px', background:'#3B82F6', borderRadius:'50%'}}></span> NIGHT SHIFT</div>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            <TimeScrollSelect label="Start Time" value24h={nightStart24h} onChange24h={setNightStart24h} />
                            <TimeScrollSelect label="End Time" value24h={nightEnd24h} onChange24h={setNightEnd24h} />
                        </div>
                    </div>
                </div>
                <div><label style={labelStyle}>Default Rate (₹ / Meter)</label><input type="number" style={{...inputStyle, color: '#10B981', fontSize: '18px'}} value={formData.base_rate} onChange={e => setFormData({...formData, base_rate: e.target.value})} /></div>
                
              </div>
            </div>

            <div className="ui-card" style={{ background: '#ffffff', borderRadius: '24px', padding: '20px', border: '1px solid #E2E8F0', boxShadow: '0 4px 15px rgba(0,0,0,0.03)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', borderBottom: '1px solid #F1F5F9', paddingBottom: '12px' }}>
                    <div style={{ background: 'linear-gradient(135deg, #DCFCE7, #86EFAC)', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 6px rgba(22, 163, 74, 0.15)' }}>
                        <MessageCircle size={16} color="#16A34A" />
                    </div>
                    <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A', margin: 0 }}>Help & Support</h3>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <SettingsItem icon={<MessageCircle size={20} color="#10B981" />} title="WhatsApp Support" subtitle="+91 73786 75430" onClick={() => window.open('https://wa.me/917378675430')} />
                    <SettingsItem icon={<PhoneCall size={20} color="#2563EB" />} title="Call Us" subtitle="+91 73786 75430" onClick={() => window.open('tel:7378675430')} />
                    <SettingsItem icon={<Mail size={20} color="#D97706" />} title="Email Support" subtitle="armomin512@gmail.com" onClick={() => window.open('mailto:armomin512@gmail.com')} /> 
                </div>

                {/* --- LOGOUT BUTTON MOVED HERE --- */}
                <div style={{ marginTop: '16px', borderTop: '1px solid #F1F5F9', paddingTop: '16px' }}>
                    <button onClick={() => { if(window.confirm("Are you sure you want to log out?")) onLogout(); }} style={{ width: '100%', padding: '16px', borderRadius: '16px', border: '1px solid rgba(239, 68, 68, 0.3)', background: '#FEF2F2', color: '#DC2626', fontWeight: '700', fontSize: '14px', cursor: 'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', boxShadow: '0 4px 15px rgba(220,38,38,0.1)', transition: 'all 0.2s' }}>
                        <LogOut size={20} strokeWidth={2.5} /> LOG OUT ACCOUNT
                    </button>
                </div>
            </div>
          </div>
        )}

       {/* ---------------- SETTINGS SECTION ---------------- */}
        {activeTab === 'settings' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* 🚀 PREMIUM APPEARANCE ENGINE */}
            <div className="ui-card" style={{ background: '#ffffff', borderRadius: '24px', padding: '20px', border: '1px solid #E2E8F0', boxShadow: '0 4px 15px rgba(0,0,0,0.03)' }}>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', borderBottom: '1px solid #F1F5F9', paddingBottom: '12px' }}>
                  <div style={{ background: 'linear-gradient(135deg, #F3E8FF, #D8B4FE)', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 6px rgba(126, 34, 206, 0.15)' }}>
                      <LayoutTemplate size={16} color="#7E22CE" />
                  </div>
                  <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A', margin: 0 }}>Appearance & Animations</h3>
              </div>

              {/* App Font Styling */}
              <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}><Type size={14} /> Global Font Family</label>
                  <div style={{display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px'}}>
                      {fonts.map(f => (
                          <button key={f} onClick={() => updateFont(f)} style={{ whiteSpace:'nowrap', background: appFont === f ? '#1A1A1A' : '#F8FAFC', color: appFont === f ? 'white' : '#0F172A', padding: '10px 14px', borderRadius: '12px', border: appFont === f ? '1px solid #1A1A1A' : '1px solid #E2E8F0', fontWeight: '600', fontSize: '12px', cursor: 'pointer', fontFamily: f }}>
                              {f}
                          </button>
                      ))}
                  </div>
              </div>

              {/* VOICE AI CONTROLLER */}
              <div style={{ marginBottom: '20px', background: 'rgba(37, 99, 235, 0.05)', padding: '16px', borderRadius: '16px', border: '1px solid rgba(37, 99, 235, 0.2)' }}>
                  <h4 style={{ fontSize: '13px', fontWeight: '700', color: '#2563EB', margin: '0 0 14px 0', display: 'flex', alignItems: 'center', gap: '8px' }}><Volume2 size={16}/> Voice Assistant Setup</h4>
                  
                  <label style={labelStyle}>Voice Speed ({voiceSpeed}x)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
                      <span style={{ fontSize: '11px', fontWeight: '700', color: '#0F172A' }}>Slow</span>
                      <input 
                          type="range" min="0.5" max="2.0" step="0.1" 
                          value={voiceSpeed} 
                          onChange={(e) => updateVoiceSettings('voiceSpeed', e.target.value, setVoiceSpeed)} 
                          style={{ flex: 1, accentColor: '#2563EB', cursor: 'pointer' }} 
                      />
                      <span style={{ fontSize: '11px', fontWeight: '700', color: '#0F172A' }}>Fast</span>
                  </div>

                  <label style={labelStyle}>Select Best Female Voice</label>
                  <select style={{...inputStyle, background: 'white'}} value={selectedVoiceURI} onChange={(e) => updateVoiceSettings('selectedVoiceURI', e.target.value, setSelectedVoiceURI)}>
                      <option value="">System Default</option>
                      {availableVoices.map((v, i) => (
                          <option key={i} value={v.voiceURI}>{v.name}</option>
                      ))}
                  </select>
              </div>

              
            </div>

            

            {/* Display & Alerts */}
            <div className="ui-card" style={{ background: '#ffffff', borderRadius: '24px', padding: '20px', border: '1px solid #E2E8F0', boxShadow: '0 4px 15px rgba(0,0,0,0.03)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', borderBottom: '1px solid #F1F5F9', paddingBottom: '12px' }}>
                  <div style={{ background: 'linear-gradient(135deg, #FCE7F3, #F9A8D4)', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 6px rgba(190, 24, 93, 0.15)' }}>
                      <ImageIcon size={16} color="#BE185D" />
                  </div>
                  <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A', margin: 0 }}>Display & Alerts</h3>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', padding: '14px', background: '#F8FAFC', borderRadius: '16px', border: '1px solid #E2E8F0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}><div style={{background: 'white', padding: '8px', borderRadius: '10px', display: 'flex', border: '1px solid #E2E8F0'}}><Bell size={16} color="#0F172A" /></div><div><div style={{ fontSize: '13px', fontWeight: '700', color: '#0F172A' }}>App Notifications</div><div style={{ fontSize: '10px', color: '#64748B', fontWeight: '600' }}>Sound & Alerts</div></div></div>
                <div onClick={() => setNotifOn(!notifOn)} style={{ width: '48px', height: '26px', background: notifOn ? '#10B981' : '#CBD5E1', borderRadius: '20px', position: 'relative', cursor: 'pointer', transition: 'all 0.3s ease', flexShrink: 0 }}><div style={{ width: '22px', height: '22px', background: 'white', borderRadius: '50%', position: 'absolute', top: '2px', left: notifOn ? '24px' : '2px', transition: 'all 0.3s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></div>
              </div>
              <div>
                  <label style={labelStyle}>Change Wallpaper</label>
                  <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '10px', padding: '16px', background: '#F8FAFC', borderRadius: '16px', border: '1px solid #E2E8F0'}}>
                      {wallpapers.map(wp => (
                          <div key={wp.id} onClick={() => setAppBg(wp.color)} title={wp.name} style={{ width: '36px', height: '36px', borderRadius: '50%', background: wp.color, cursor: 'pointer', border: appBg === wp.color ? '3px solid #1A1A1A' : '3px solid white', boxShadow: appBg === wp.color ? '0 4px 12px rgba(0,0,0,0.3)' : '0 2px 6px rgba(0,0,0,0.1)', transition: 'all 0.2s ease', transform: appBg === wp.color ? 'scale(1.15)' : 'scale(1)' }} />
                      ))}
                  </div>
              </div>
            </div>

            {/*  Automation */}
            <div className="ui-card" style={{ background: '#ffffff', borderRadius: '24px', padding: '20px', border: '1px solid #E2E8F0', boxShadow: '0 4px 15px rgba(0,0,0,0.03)' }}>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', borderBottom: '1px solid #F1F5F9', paddingBottom: '12px' }}>
                  <div style={{ background: 'linear-gradient(135deg, #E0E7FF, #A5B4FC)', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 6px rgba(67, 56, 202, 0.15)' }}>
                      <Smartphone size={16} color="#4338CA" />
                  </div>
                  <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A', margin: 0 }}> Automation</h3>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', padding: '14px', background: '#F8FAFC', borderRadius: '16px', border: '1px solid #E2E8F0' }}>
                <div><div style={{ fontSize: '13px', fontWeight: '700', color: '#0F172A' }}>Auto WhatsApp Reports</div><div style={{ fontSize: '10px', color: '#64748B', fontWeight: '600' }}>Send shift closing data</div></div>
                <div onClick={() => setFormData({...formData, whatsapp_reports: formData.whatsapp_reports === 1 ? 0 : 1})} style={{ width: '48px', height: '26px', background: formData.whatsapp_reports === 1 ? '#10B981' : '#CBD5E1', borderRadius: '20px', position: 'relative', cursor: 'pointer', transition: 'all 0.3s ease', flexShrink: 0 }}><div style={{ width: '22px', height: '22px', background: 'white', borderRadius: '50%', position: 'absolute', top: '2px', left: formData.whatsapp_reports === 1 ? '24px' : '2px', transition: 'all 0.3s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></div>
              </div>

            </div>
          </div>
        )}

      </div>

      <div style={{ position: 'fixed', bottom: '0', left: '0', width: '100%', padding: '16px 20px', boxSizing: 'border-box', zIndex: 100, background: 'linear-gradient(to top, rgba(255,255,255,0.9), transparent)', backdropFilter: 'blur(10px)' }}>
        <button onClick={(e) => { triggerHaptic([50, 100, 50]); handleSave(e); }} disabled={loading} style={{ width: '100%', padding: '18px', borderRadius: '16px', background: '#1A1A1A', color: 'white', fontWeight: '700', fontSize: '14px', letterSpacing: '1px', border: 'none', cursor: 'pointer', boxShadow: '0 10px 30px rgba(0,0,0,0.25)', transition: 'transform 0.2s', transform: loading ? 'scale(0.98)' : 'scale(1)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {loading ? 'SAVING DATA...' : 'SAVE ALL CHANGES'}
        </button>
      </div>
    </div>
  );
};

export default ProfileSettings;